/*
 * ExportMaintenanceSection — Feb 2026
 * -----------------------------------
 * Super-admin-only Maintenance panel for the Export Orders
 * dashboard. Wires up:
 *   * Global maintenance mode toggle (503s the public timeline share view)
 *   * Notification History table (searchable audit trail of every
 *     export-related email — Notify Plant, auto-fired plant
 *     shortfalls, stage-completed dept blasts, timeline updates)
 *   * Maintenance tools: Clear All / Clear Selected notification
 *     history rows, Rebuild All Timelines
 *
 * The parent (AdminExportDashboard) is expected to gate rendering
 * on `isSuperAdmin` — this component itself trusts its caller and
 * does not double-check.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  ShieldAlert, Wrench, Mail, RefreshCw, Trash2, Loader2, Search, CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { formatDateTime as fmtDT } from '../lib/dateFormat';

export const ExportMaintenanceSection = ({ API, authHeaders, toast }) => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState('');

  const loadState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/export-orders/maintenance`, { headers: authHeaders });
      setMaintenanceMode(!!data?.maintenance_mode);
    } catch (_e) { /* ignore — panel just shows OFF */ }
  }, [API, authHeaders]);

  const loadLog = useCallback(async () => {
    setLoadingLog(true);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/admin/export-orders/notification-log`, {
        headers: authHeaders, params,
      });
      setLog(data?.entries || []);
      setSelected(new Set());
    } catch (e) {
      toast?.({ title: 'Could not load notification log', description: e?.response?.data?.detail || String(e) });
    } finally { setLoadingLog(false); }
  }, [API, authHeaders, q, statusFilter, toast]);

  useEffect(() => { loadState(); loadLog(); /* eslint-disable-line */ }, []);

  const saveMaintenanceMode = async (next) => {
    const prev = maintenanceMode;
    setMaintenanceMode(next);
    setSavingMaintenance(true);
    try {
      await axios.put(`${API}/admin/export-orders/maintenance`, { maintenance_mode: !!next }, { headers: authHeaders });
      toast?.({
        title: next ? 'Maintenance mode ON' : 'Maintenance mode OFF',
        description: next
          ? 'Public timeline share view now shows the maintenance screen.'
          : 'Public timeline share view is back online.',
      });
    } catch (e) {
      setMaintenanceMode(prev);
      toast?.({ title: 'Could not save', description: e?.response?.data?.detail || String(e) });
    } finally { setSavingMaintenance(false); }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === log.length) setSelected(new Set());
    else setSelected(new Set(log.map((r) => r.id)));
  };

  const clearAll = async () => {
    if (!window.confirm('Delete ALL notification history rows? This cannot be undone.')) return;
    setBusy('clear-all');
    try {
      const { data } = await axios.delete(`${API}/admin/export-orders/notification-log`, { headers: authHeaders });
      toast?.({ title: `Cleared ${data?.deleted ?? 0} rows` });
      await loadLog();
    } catch (e) {
      toast?.({ title: 'Clear failed', description: e?.response?.data?.detail || String(e) });
    } finally { setBusy(''); }
  };

  const clearSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected row(s)?`)) return;
    setBusy('clear-selected');
    try {
      const { data } = await axios.post(
        `${API}/admin/export-orders/notification-log/delete-selected`,
        { ids: Array.from(selected) },
        { headers: authHeaders },
      );
      toast?.({ title: `Deleted ${data?.deleted ?? 0} row(s)` });
      await loadLog();
    } catch (e) {
      toast?.({ title: 'Delete failed', description: e?.response?.data?.detail || String(e) });
    } finally { setBusy(''); }
  };

  const rebuildTimelines = async () => {
    if (!window.confirm('Rebuild timelines on every export order? This is non-destructive.')) return;
    setBusy('rebuild');
    try {
      const { data } = await axios.post(
        `${API}/admin/export-orders/rebuild-timelines`, {},
        { headers: authHeaders },
      );
      const s = data?.summary || {};
      toast?.({
        title: 'Timelines rebuilt',
        description: `Scanned ${s.orders_scanned || 0} · touched ${s.orders_touched || 0} · added ${s.rows_added || 0} · deduped ${s.rows_deduped || 0}`,
      });
    } catch (e) {
      toast?.({ title: 'Rebuild failed', description: e?.response?.data?.detail || String(e) });
    } finally { setBusy(''); }
  };

  const kindPill = (kind) => {
    const label = ({
      notify_plant: 'Notify Plant',
      auto_plant_shortfall: 'Auto Shortfall',
      stage_completed: 'Stage Completed',
      timeline_update: 'Timeline Update',
    })[kind] || kind;
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
        {label}
      </span>
    );
  };
  const statusPill = (s, err) => {
    if (s === 'sent') return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase" data-testid={`export-maint-log-status-${s}`}><CheckCircle2 className="w-3 h-3" /> Sent</span>;
    if (s === 'failed') return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200 px-2 py-0.5 text-[10px] font-bold uppercase" title={err || ''} data-testid={`export-maint-log-status-${s}`}><AlertTriangle className="w-3 h-3" /> Failed</span>;
    return <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200 px-2 py-0.5 text-[10px] font-bold uppercase" data-testid={`export-maint-log-status-${s}`}>{s || '—'}</span>;
  };

  return (
    <section className="mb-6" data-testid="export-maintenance-section">
      {/* Toggle */}
      <div
        className={`rounded-2xl border px-4 py-3 flex items-center gap-4 mb-3 ${
          maintenanceMode ? 'border-amber-300 bg-amber-50' : 'border-neutral-200 bg-white'
        }`}
        data-testid="export-maintenance-row"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <ShieldAlert className={`w-4 h-4 ${maintenanceMode ? 'text-amber-600' : 'text-neutral-400'}`} />
            <div className="text-sm font-semibold text-[#0F172A]">Export Orders Maintenance Mode</div>
            {maintenanceMode && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase"
                data-testid="export-maintenance-badge-active"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> Active
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5 leading-snug">
            When ON, every viewer of the Export Timeline share links sees a branded &ldquo;Under Maintenance&rdquo; screen instead of the timeline.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-neutral-500" data-testid="export-maintenance-state-label">
            {maintenanceMode ? 'ON' : 'OFF'}
          </span>
          <Switch
            checked={maintenanceMode}
            disabled={savingMaintenance}
            onCheckedChange={saveMaintenanceMode}
            data-testid="export-maintenance-toggle"
            aria-label="Export Orders Maintenance Mode"
          />
        </div>
      </div>

      {/* Notification History + Tools */}
      <div className="rounded-2xl border border-neutral-200 bg-white" data-testid="export-notification-history">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100">
          <Mail className="w-4 h-4 text-[#0078D4]" />
          <div className="text-sm font-semibold text-[#0F172A] flex-1">Notification History</div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadLog(); }}
              placeholder="Search recipients, subject, order # …"
              className="pl-7 h-8 w-64 text-xs"
              data-testid="export-maint-log-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 px-2 rounded-md border border-neutral-300 bg-white text-xs"
            data-testid="export-maint-log-status-filter"
          >
            <option value="">All status</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
          <Button variant="outline" size="sm" onClick={loadLog} disabled={loadingLog} data-testid="export-maint-log-refresh">
            {loadingLog ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-neutral-100 bg-neutral-50/60">
          <Button
            variant="outline" size="sm"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={clearSelected}
            disabled={selected.size === 0 || busy === 'clear-selected'}
            data-testid="export-maint-clear-selected"
          >
            {busy === 'clear-selected' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
            Clear Selected ({selected.size})
          </Button>
          <Button
            variant="outline" size="sm"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={clearAll}
            disabled={log.length === 0 || busy === 'clear-all'}
            data-testid="export-maint-clear-all"
          >
            {busy === 'clear-all' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
            Clear All
          </Button>
          <Button
            variant="outline" size="sm"
            className="border-[#0078D4]/40 text-[#0078D4] hover:bg-[#F0F9FF] ml-auto"
            onClick={rebuildTimelines}
            disabled={busy === 'rebuild'}
            data-testid="export-maint-rebuild-timelines"
          >
            {busy === 'rebuild' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1" />}
            Rebuild All Timelines
          </Button>
        </div>

        {/* Table */}
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="export-maint-log-table">
            <thead className="bg-neutral-50 border-b border-neutral-100 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 w-8 text-left">
                  <Checkbox
                    checked={log.length > 0 && selected.size === log.length}
                    onCheckedChange={toggleAll}
                    disabled={log.length === 0}
                    aria-label="Select all"
                    data-testid="export-maint-log-select-all"
                  />
                </th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-neutral-500">Timestamp</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-neutral-500">Recipient(s)</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-neutral-500">Category · Dept</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-neutral-500">Subject</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-neutral-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingLog && log.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-neutral-400"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</td></tr>
              )}
              {!loadingLog && log.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-neutral-400" data-testid="export-maint-log-empty">No notifications logged yet.</td></tr>
              )}
              {log.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50/60" data-testid={`export-maint-log-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggleRow(r.id)}
                      aria-label="Select row"
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-neutral-600">{r.ts ? fmtDT(r.ts) : '—'}</td>
                  <td className="px-3 py-2 text-neutral-700">{(r.recipients || []).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-neutral-600">
                    <div className="flex items-center gap-1">{kindPill(r.kind)}</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      {(r.category || '—')}{r.department ? ` · ${r.department}` : ''}
                      {r.order_no ? ` · ${r.order_no}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-neutral-700 max-w-md truncate" title={r.subject || ''}>{r.subject || '—'}</td>
                  <td className="px-3 py-2">{statusPill(r.status, r.error)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ExportMaintenanceSection;
